import { toast } from '@/hooks/use-toast'
import type { MultiProductOrderTreatment } from '@/lib/stores/medication-request'
import { checkMedicationDirectionsLength, isFutureIssueDate } from '@/lib/utils'
import type { MedRequest } from '@/types/data'
import { actionPlanOf, isOpenedTreatmentPending } from '@/lib/med-requests'
import type { ApprovePlan } from '../approve'
import {
  isMultiProductOrderTreatmentComplete,
  multiProductOrderFamilyApproveActions,
  type OpenedTreatmentState,
  openedApproveAction,
  pendingMultiProductOrderFamilyTreatments,
  withPageCsNote
} from './family-actions'

/**
 * "Approve all": the opened request (unless already decided on its card), then every other pending
 * item from its own card. Null when a check failed; it toasts why.
 */
export const multiProductOrderApprovePlan = (
  medRequest: MedRequest,
  {
    opened,
    treatments,
    reason
  }: { opened: OpenedTreatmentState; treatments: readonly MultiProductOrderTreatment[]; reason: string }
): ApprovePlan | null => {
  // Re-checked: a card can be cleared while the approval sidebar is open.
  const pendingFamilyTreatments = pendingMultiProductOrderFamilyTreatments(medRequest, treatments)
  if (pendingFamilyTreatments.some(t => !isMultiProductOrderTreatmentComplete(t))) {
    toast({
      title: 'Approval not sent',
      description: 'Every treatment needs a medication and directions before approving them all.'
    })
    return null
  }

  const isOpenedPending = isOpenedTreatmentPending(medRequest)

  // Checked up front so the API can't refuse one after others were approved.
  const directions = [
    ...(isOpenedPending ? [opened.medicationDirection] : []),
    ...pendingFamilyTreatments.map(t => t?.direction ?? '')
  ]
  if (!directions.every(direction => checkMedicationDirectionsLength(direction))) return null

  // This layout has no temporary replacement or last subscription order checkbox.
  const openedAction = openedApproveAction(medRequest, opened, reason)
  const actions = withPageCsNote(medRequest, opened.notes, [
    ...(isOpenedPending ? [openedAction] : []),
    ...multiProductOrderFamilyApproveActions(medRequest, {
      treatments,
      reason,
      notes: opened.notes,
      issueDate: opened.medicationIssueDate
    })
  ])

  return {
    ...actionPlanOf(actions, openedAction),
    isForFuture: isFutureIssueDate(opened.medicationIssueDate)
  }
}
